import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";

import { createSelector } from "reselect";

import { hireShape } from "lib/propTypes";
import { HIRE_STATE, QUESTION_FLOW } from "lib/constants";
import { ReplayIcon } from "common/DS/Icons";
import Linkify from "common/DS/Linkify";
import { localQuestions, getCurrentQuestion } from "redux/questions/selectors";
import { checkinReset } from "redux/checkin/actions";
import Questions from "candidate/Questions";
import CheckInSummary from "candidate/CheckIn/CheckInSummary";
import CheckInLanguageSelector from "candidate/CheckIn/CheckInLanguageSelector";
import CheckInHeader from "candidate/CheckIn/CheckInHeader";
import CandidatePageWrapper from "candidate/CandidatePageWrapper";
import { ContainedButton, TextButton } from "common/DS/Buttons";
import KioskCountDownTimer from "candidate/Kiosk/KioskCountDownTimer";
import Dialog from "common/DS/Dialogs";
import { useIdle } from "lib/hooks";
import sanitizeHtml from "sanitize-html";
import LogOut, { useLogout } from "./LogOut";
import CheckInClosed from "./CheckIn/CheckInClosed";
import { ErrorPage } from "./ErrorState";

const IDLE_TIME_MS = 30 * 1000;

const selector = createSelector(
    (state) => state.answers,
    (state) => localQuestions(state),
    (state) => getCurrentQuestion(state),
    (state) => state.company?.settings?.retain?.disclaimer,
    (state) => state.isKiosk,
    (state) => state.kioskHireUUID,
    (state) => state.questions,
    (state) => localQuestions(state).length,
    (state) => state.company?.name,
    (state) => state.is_preview,
    (state) => state.ws.demo.enabled,
    (state) => state.i18nState.lang,
    (
        answers,
        allQuestions,
        question,
        disclaimerText,
        isKiosk,
        kioskHireUUID,
        questions,
        questionsLength,
        companyName,
        isPreview,
        isDemo,
        lang,
    ) => [
        answers,
        allQuestions,
        question ? [question] : [],
        disclaimerText,
        isKiosk,
        kioskHireUUID,
        questions.current === null ? null : questions.current + 1,
        questionsLength,
        companyName,
        isPreview,
        isDemo,
        lang,
    ],
);

export default function CandidateCheckIn(
    {
        hire,
        source,
        checkinId,
        checkinHireId,
        checkinHash,
        checkinType,
        checkinThankYouTitle,
        checkinThankYouMessage,
        checkinExpired,
        checkinEndTime,
        checkinRemoveConfidentiality,
        checkinNonConfidential,
        showDisclaimerTest,
        disclaimerTestBody,
    },
    { t: _ },
) {
    const [
        answers,
        allQuestions,
        questions,
        disclaimerText,
        isKiosk,
        kioskHireUUID,
        current,
        questionCount,
        companyName,
        isPreview,
        isDemo,
        lang,
    ] = useSelector(selector);
    const dispatch = useDispatch();
    const [isAnonymous, setIsAnonymous] = useState(!checkinNonConfidential);
    const [hireId, setHireId] = useState(hire?.id);
    const [localCheckinHireId, setLocalCheckinHireId] = useState(checkinHireId);
    const [localCheckinHash, setLocalCheckinHash] = useState(checkinHash);
    const [hireState, setHireState] = useState(hire?.state);

    const hireIsValid = hireState !== HIRE_STATE.INVALID;
    const errorTitle = "Account Not Recognized";
    const errorText = "It looks like you've stumbled upon a survey you don't have access to.";

    const renderSummary = () => (
        <CheckInSummary
            companyName={companyName}
            checkinHireId={localCheckinHireId}
            checkinHash={localCheckinHash}
            checkinType={checkinType}
            checkinThankYouTitle={checkinThankYouTitle}
            checkinThankYouMessage={checkinThankYouMessage}
        />
    );
    const renderLanguageSelect = () => !isKiosk && hire && <CheckInLanguageSelector hire={hire} />;

    const toggleAnonymous = (val = !isAnonymous) => {
        if (checkinNonConfidential) {
            return;
        }
        setIsAnonymous(val);
    };

    useEffect(() => {
        // If the hire or kiosk uuid changes then update or clear the hire and checkin hire ids
        setHireId(hire?.id);
        setLocalCheckinHireId(checkinHireId);
        setLocalCheckinHash(checkinHash);
        setHireState(hire?.state);
    }, [hire, checkinHireId, checkinHash, kioskHireUUID]);

    // When in kiosk mode, if we get an answer back that matches our existing kiosk uuid then
    // we can update local state with the relevant hire and checkin hire data
    const handleAnswerResponse = (resp) => {
        if (!resp.hire_id || resp.kiosk_uuid !== kioskHireUUID) {
            return;
        }

        setHireId(resp.hire_id);
        setLocalCheckinHireId(resp.checkin_hire_id);
        setLocalCheckinHash(resp.checkin_hash);
    };

    const isSms = window.location.pathname.startsWith("/m/");
    const isLastQuestion = current === questionCount;
    const isFirstQuestion = current === 1;
    const extraData = {
        checkin_id: checkinId,
        checkin_hire_id: localCheckinHireId,
        hire_id: hireId,
        is_preview: isPreview,
        is_demo: isDemo,
        is_sms: isSms,
    };
    if (hire) {
        extraData.user = {
            ...(hire.user || {}),
            id: hire.user_id,
            email: hire.user?.email || "",
        };
    }
    const showCheckinDetails = !checkinExpired || isPreview;

    const params = new URLSearchParams(window.location.search);
    const showLogout = params.get("utm_source") === "hub";
    const showCountDown =
        showLogout &&
        ((answers && Object.keys(answers).length === allQuestions.length) ||
            questions.length === 0);

    const showRestartButton = isPreview && !isKiosk && !isFirstQuestion;
    const footerClass = classNames("check-in__lower", {
        "check-in__privacy-policy-with-logout": showLogout,
    });
    const doRestart = () => dispatch(checkinReset());

    const [doLogout] = useLogout(checkinHash);
    const [showInactiveModal, setShowInactiveModal] = useState(false);

    const disclaimerEnabled =
        disclaimerTestBody.length > 0 &&
        (!lang || lang === "en") &&
        (showDisclaimerTest ||
            new URLSearchParams(window.location.search).get("disclaimer") === "true");
    const [showDisclaimerDialog, setShowDisclaimerDialog] = useState(disclaimerEnabled);

    // const onStartPage = useSelector((state) => state.questions.current === 0);
    const isIdle = useIdle({ timeToIdle: IDLE_TIME_MS, inactivityEvents: [] });

    useEffect(() => {
        if (showLogout && !showCountDown) {
            setShowInactiveModal(isIdle);
        }
    }, [isIdle]);

    const onHideInactiveModal = () => setShowInactiveModal(false);

    const isPageReady = hireIsValid && showCheckinDetails && questionCount > 0;

    return (
        <>
            <Dialog
                open={isPageReady && showDisclaimerDialog}
                title="Welcome"
                onClose={() => {}}
                dividers={false}
                className="disclaimer-dialog"
                showCloseIcon={false}
                actionsRight={[
                    <ContainedButton onClick={() => setShowDisclaimerDialog(false)}>
                        Acknowledge
                    </ContainedButton>,
                ]}
            >
                <div
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(disclaimerTestBody, {
                            allowedTags: ["p", "strong"],
                        }),
                    }}
                />
            </Dialog>
            <Dialog
                open={showInactiveModal}
                title={_("Are you still there?")}
                handleHide={onHideInactiveModal}
                PaperProps={{ className: "kiosk-timeout-dialog" }}
                dividers={false}
                actionsRight={[
                    <TextButton onClick={doLogout}>{_("Start over")}</TextButton>,
                    <ContainedButton onClick={onHideInactiveModal}>
                        {_("Keep working")}
                    </ContainedButton>,
                ]}
            >
                <KioskCountDownTimer isIdle={isIdle} doReset={doLogout} />
            </Dialog>
            <CandidatePageWrapper contentClassName="check-in">
                {!hireIsValid && (
                    <ErrorPage
                        errorTitle={errorTitle}
                        errorText={errorText}
                        companyName={companyName}
                    />
                )}
                {hireIsValid && (
                    <>
                        {current && questionCount > 0 && showCheckinDetails && (
                            <CheckInHeader
                                isKiosk={isKiosk}
                                checkinType={checkinType}
                                isAnonymous={isAnonymous}
                                questionCount={questionCount}
                                current={current}
                                checkinRemoveConfidentiality={checkinRemoveConfidentiality}
                            />
                        )}
                        {!showCheckinDetails && (
                            <div className="check-in__closed-container">
                                <div className="check-in__closed">
                                    <CheckInClosed
                                        endDate={checkinEndTime}
                                        companyName={companyName}
                                        renderLanguageSelect={renderLanguageSelect}
                                    />
                                </div>
                            </div>
                        )}
                        {showCheckinDetails && (
                            <Questions
                                autoSelect
                                canSkip
                                canUndo={!isKiosk}
                                className="check-in__questions"
                                extraData={extraData}
                                flow={QUESTION_FLOW.CHECK_IN}
                                renderEmpty={renderSummary}
                                source={source}
                                renderLanguageSelect={renderLanguageSelect}
                                isAnonymous={isAnonymous}
                                toggleAnonymous={toggleAnonymous}
                                handleAnswerResponse={handleAnswerResponse}
                                isLastQuestion={isLastQuestion}
                                checkinRemoveConfidentiality={checkinRemoveConfidentiality}
                                checkinNonConfidential={checkinNonConfidential}
                            />
                        )}
                        {showCheckinDetails && (
                            <>
                                <div className={footerClass}>
                                    {showRestartButton && (
                                        <div className="check-in__restart_button">
                                            <TextButton
                                                endIcon={<ReplayIcon />}
                                                onClick={doRestart}
                                            >
                                                Restart
                                            </TextButton>
                                        </div>
                                    )}
                                    {showLogout && (
                                        <LogOut
                                            checkinHash={checkinHash}
                                            showCountDown={showCountDown}
                                        />
                                    )}
                                </div>
                                {disclaimerText && (
                                    <div className="check-in__disclaimer">
                                        <Linkify>{disclaimerText}</Linkify>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </CandidatePageWrapper>
        </>
    );
}

CandidateCheckIn.contextTypes = {
    t: PropTypes.func,
};

CandidateCheckIn.propTypes = {
    hire: hireShape,
    source: PropTypes.number.isRequired,
    checkinId: PropTypes.number,
    checkinHireId: PropTypes.number,
    checkinHash: PropTypes.string,
    checkinType: PropTypes.number,
    checkinExpired: PropTypes.bool,
    checkinEndTime: PropTypes.string,
    checkinThankYouTitle: PropTypes.string,
    checkinThankYouMessage: PropTypes.string,
    checkinRemoveConfidentiality: PropTypes.bool,
    checkinNonConfidential: PropTypes.bool,
    showDisclaimerTest: PropTypes.bool,
    disclaimerTestBody: PropTypes.string,
};

CandidateCheckIn.defaultProps = {
    checkinId: null,
    checkinHash: null,
    checkinHireId: null,
    hire: null,
    checkinType: null,
    checkinExpired: false,
    checkinEndTime: null,
    checkinThankYouTitle: null,
    checkinThankYouMessage: null,
    checkinRemoveConfidentiality: false,
    checkinNonConfidential: false,
    showDisclaimerTest: false,
    disclaimerTestBody: "",
};
